import { useState } from 'react';
import { formatarDocumento } from './segmentoTriagem';
import { Clock, Phone, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { TimelineUnificada } from '@/components/timeline/TimelineUnificada';
import { ClickToCall } from '@/components/common/ClickToCall';
import { useSoftphoneOptional } from '@/components/softphone/SoftphoneProvider';
import { BotaoConversaWhats } from '@/components/planilha/BotaoConversaWhats';
import { CancelarItemModal } from '@/components/pedido/CancelarItemModal';
import { getBkoTeamIds, getUserAccessibleTeamIds } from '@/lib/fila-permissions';
import { TRIAGEM_BTN } from '@/components/kanban/triagem/triagemTabela';

export interface ItemTriagemBase {
  item_id: string;
  produto: string;
  quantidade: number;
  subtotal: number;
}

/**
 * MESMO escopo do Kanban (useKanbanDataOptimized) aplicado às triagens:
 * consultor = só os próprios; bko/qualidade = equipes mapeadas em
 * bko_equipe_responsavel (sem mapa = tudo) — o BKO NUNCA vê equipe que não é
 * dele (Douglas 29/09/2026);
 * supervisor/coordenador = equipes acessíveis (sem mapa = só os próprios);
 * master/diretor/gerente = tudo.
 */
export async function aplicarEscopoTriagem(
  query: any,
  profile: { id?: string; role?: string | null; funcao?: string | null } | null | undefined,
) {
  const userId = profile?.id;
  const role = String(profile?.role || '').toLowerCase();
  const funcao = String((profile as any)?.funcao || '').toUpperCase();
  if (!userId || !role) return query;
  if (role === 'consultor') return query.eq('consultor_id', userId);
  if (['bko', 'qualidade'].includes(role)) {
    const ids = await getBkoTeamIds(userId);
    if (ids.length > 0) return query.in('equipe_id', ids);
    return query;
  }
  if (['supervisor', 'coordenador'].includes(role)) {
    const ids = await getUserAccessibleTeamIds(userId, role);
    if (ids.length > 0) return query.in('equipe_id', ids);
    return query.eq('consultor_id', userId);
  }
  return query;
}

/**
 * A SUPERVISORA VÊ AS TRIAGENS DA EQUIPE DELA (Douglas 14/10/2026): "liberar a
 * visão da triagem para que os supervisores vejam seus pedidos e dos seus
 * times". O gateway não recorta a v_planilha_mae para o líder, então a fila
 * que não passa por aplicarEscopoTriagem recorta aqui. Só mexe no
 * supervisor/coordenador: BKO, Qualidade e Retenção continuam como estavam.
 * null = não é líder (não filtra).
 */
export async function equipesDoLider(
  profile: { id?: string | null; role?: string | null } | null | undefined,
): Promise<string[] | null> {
  const role = String(profile?.role || '').toLowerCase();
  if (!profile?.id || !['supervisor', 'coordenador'].includes(role)) return null;
  return getUserAccessibleTeamIds(profile.id, role);
}

type FiltroDoLider = { in: (c: string, v: string[]) => unknown; eq: (c: string, v: string) => unknown };

export function filtrarPeloLider<Q>(
  query: Q,
  profile: { id?: string | null } | null | undefined,
  equipes: string[] | null,
): Q {
  if (!equipes) return query;
  const q = query as unknown as FiltroDoLider;
  if (equipes.length > 0) return q.in('equipe_id', equipes) as Q;
  return q.eq('consultor_id', String(profile?.id)) as Q;
}

/**
 * ⚠️ Mascara CNPJ **e CPF**: a régua é `segmentoTriagem.formatarDocumento`, que
 * olha o TAMANHO do documento, não o nome da coluna. Antes só entendia 14
 * dígitos — e o pedido de CPF aparecia como "—" na fila.
 */
export function formatCnpjTriagem(cnpj: string): string {
  return formatarDocumento(cnpj);
}

/** Resumo dos produtos do pedido em UMA linha: "PN NOVO ×5 · TT POS-PJ" */
export function resumoProdutos(itens: ItemTriagemBase[]): { resumo: string; completo: string } {
  const contagem = new Map<string, number>();
  for (const i of itens) {
    const nome = i.produto || 'Item';
    contagem.set(nome, (contagem.get(nome) || 0) + 1);
  }
  const partes = Array.from(contagem.entries()).map(([nome, n]) => (n > 1 ? `${nome} ×${n}` : nome));
  const completo = itens
    .map((i) => `${i.produto || 'Item'} — R$ ${Number(i.subtotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`)
    .join('\n');
  return { resumo: partes.join(' · '), completo };
}

/** Timeline do CNPJ — mesmo Sheet lateral da Planilha de Pedidos */
export function TimelineCnpjButton({ cnpj }: { cnpj?: string | null }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className={TRIAGEM_BTN}
        title="Timeline do CNPJ"
        onClick={(e) => { e.stopPropagation(); if (cnpj) setOpen(true); }}
        disabled={!cnpj}
      >
        <Clock className="h-3 w-3" />
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="sm:max-w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Timeline — {cnpj}</SheetTitle>
          </SheetHeader>
          {open && cnpj && <TimelineUnificada cnpj={cnpj} compact />}
        </SheetContent>
      </Sheet>
    </>
  );
}

/**
 * Contato do pedido: ligar (click-to-call) + conversa no WhatsApp Inbox — mesmo padrão da Planilha.
 *
 * O clique vai pro softphone Prime (discador do CRM, SIP) e NÃO tem mais saída
 * por fora: quem está sem ramal, ou com o softphone caído, simplesmente NÃO
 * liga — o painel do softphone abre e um toast diz que a ligação não foi feita.
 * O fallback pro `tel:` do computador foi abolido em 06/08 (Douglas: "eu quero
 * o uso manual exclusivo do nosso softphone, não quero deixar externo"), porque
 * a ligação saía por fora do CRM, sem gravação e sem registro.
 * (Comentário corrigido em 04/09/2026: ele ainda descrevia esse fallback morto.)
 *
 * ⛔ NÃO coloque `onClick={e => e.stopPropagation()}` no Button aqui dentro.
 * O handler do filho roda ANTES do handler do <span> do ClickToCall e mata ele —
 * resultado: o softphone nunca é chamado e o clique não faz NADA, sem nem um
 * aviso. Foi o bug que a Giovana viu em 05/08 ("cliquei em discar e não abre o
 * discador"), e valia para TODAS as triagens. O próprio ClickToCall já dá
 * stopPropagation, então a linha do card não é acionada de qualquer forma.
 */
export function ContatoPedidoCell({ telefone, leadOrigemId, cnpj, telefonesAlternativos, nomeContato }: {
  telefone?: string | null;
  leadOrigemId?: string | null;
  cnpj?: string | null;
  telefonesAlternativos?: (string | null | undefined)[];
  /**
   * De quem é o telefone (na biometria, o GESTOR). Vai direto para o softphone
   * — a tela já sabe quem é, então não faz sentido o softphone adivinhar pelo
   * número. Douglas 06/08: "tem que aparecer o nome do gestor no softphone".
   */
  nomeContato?: string | null;
}) {
  const softphone = useSoftphoneOptional();
  const discadorOk = softphone?.status === 'registrado';
  const foraDoDiscador = !softphone || softphone.status === 'sem-ramal' || softphone.status === 'falha' || softphone.status === 'desconectado';
  const tituloLigar = discadorOk
    ? `Ligar pelo discador do CRM (ramal ${softphone?.ramal || '-'})${nomeContato ? ` para ${nomeContato}` : ''}: ${telefone}`
    // ⛔ Estes dois textos diziam que "a ligação vai sair por fora do CRM, sem
    // gravação" — herança do fallback `window.open('tel:')`, ABOLIDO em 06/08/2026
    // (Douglas: "quero o uso manual exclusivo do nosso softphone"). Hoje a ligação
    // simplesmente NÃO SAI: `SoftphoneProvider.call()` barra quando o status não é
    // 'registrado' e avisa por toast. Prometer que sai por fora é pior que não
    // avisar nada — o consultor esperava o discador do computador abrir e ficava
    // olhando pra tela sem entender. (04/09/2026)
    : softphone?.status === 'sem-ramal'
      ? `Você está SEM ramal no CRM — a ligação NÃO vai sair. Peça o ramal ao suporte. (${telefone})`
      : foraDoDiscador
        ? `Discador do CRM indisponível agora — a ligação NÃO vai sair. Aguarde alguns segundos e tente de novo. (${telefone})`
        : `Conectando ao discador do CRM… (${telefone})`;

  // 69a rodada 2: contato é CANAL, não desfecho — os três (fone, WhatsApp,
  // relógio) são ghost 22×22; nenhum sólido, nenhum verde.
  const GHOST_CONTATO = 'h-7 w-7 p-0 rounded-[7px] border border-[#E4DDD3] bg-[#FAF8F5] text-muted-foreground transition-colors hover:border-primary/50 hover:!text-primary dark:border-border dark:bg-white/[0.04] [&_svg]:h-[14px] [&_svg]:w-[14px]';
  return (
    <div className="flex items-center gap-2">
      {telefone ? (
        <ClickToCall telefone={telefone} nome={nomeContato ?? undefined}>
          <Button
            variant="ghost"
            size="sm"
            className={GHOST_CONTATO}
            title={tituloLigar}
          >
            <Phone className="h-3 w-3" />
          </Button>
        </ClickToCall>
      ) : (
        <Button variant="ghost" size="sm" className={`${GHOST_CONTATO} opacity-40`} disabled title="Sem telefone no pedido">
          <Phone className="h-3 w-3" />
        </Button>
      )}
      <span className="[&_button]:h-7 [&_button]:w-7 [&_button]:p-0 [&_button]:rounded-[7px] [&_button]:border [&_button]:border-[#E4DDD3] [&_button]:bg-[#FAF8F5] dark:[&_button]:border-border dark:[&_button]:bg-white/[0.04]">
        <BotaoConversaWhats
          leadOrigemId={leadOrigemId}
          telefone={telefone}
          cnpj={cnpj}
          telefonesAlternativos={telefonesAlternativos}
        />
      </span>
    </div>
  );
}

interface CancelarItemTriagemButtonProps {
  pedidoId: string;
  itens: ItemTriagemBase[];
  disabled?: boolean;
  disabledTitle?: string;
  onAfterCancel: () => void;
}

/** X de cancelar: 1 item abre direto a engrenagem atual; vários itens, escolhe qual */
export function CancelarItemTriagemButton({
  pedidoId,
  itens,
  disabled,
  disabledTitle,
  onAfterCancel,
}: CancelarItemTriagemButtonProps) {
  const [itemCancelar, setItemCancelar] = useState<ItemTriagemBase | null>(null);

  const botao = (
    <Button
      size="icon"
      variant="ghost"
      className={TRIAGEM_BTN}
      title={disabled ? (disabledTitle || 'Bloqueado') : 'Cancelar item do pedido'}
      disabled={disabled}
      onClick={itens.length === 1 ? () => setItemCancelar(itens[0]) : undefined}
    >
      <X className="h-3.5 w-3.5" />
    </Button>
  );

  return (
    <>
      {itens.length === 1 || disabled ? (
        botao
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{botao}</DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64" style={{ zIndex: 10800 }}>
            <DropdownMenuLabel className="text-xs">Cancelar qual item?</DropdownMenuLabel>
            {itens.map((item) => (
              <DropdownMenuItem
                key={item.item_id}
                className="cursor-pointer text-xs"
                onClick={() => setItemCancelar(item)}
              >
                <X className="h-3 w-3 mr-1.5 text-destructive" />
                {item.produto || 'Item'} — R$ {Number(item.subtotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {itemCancelar && (
        <CancelarItemModal
          open={!!itemCancelar}
          onOpenChange={(open) => { if (!open) setItemCancelar(null); }}
          itemId={itemCancelar.item_id}
          pedidoId={pedidoId}
          itemNome={itemCancelar.produto}
          onSuccess={onAfterCancel}
        />
      )}
    </>
  );
}
